import { users } from '~api';
import { User } from '~models';
import { Handler } from '~services';
import { getField, updateField } from 'vuex-map-fields';
import i18n from '../i18n';
import router from '~router';

const LOADING = 'LOADING';
const SET_USER = 'SET_USER';
const SET_USERS = 'SET_USERS';
const RESET_USER = 'RESET_USER';

const state = {
    user: { ...new User() },
    users: [],
    isLoading: false
};

const actions = {
    /**
     * Reset User state and redirect to Users list
     * @param {function} commit
     */
    cancel({ commit }) {
        commit(RESET_USER);
        router.push({ name: 'dashboard.users' });
    },

    /**
     * Reset User state
     * @param {function} commit
     */
    resetUser({ commit }) {
        commit(RESET_USER);
    },

    // CRUD USER
    /**
     * Create user account
     * @param {function} commit
     * @param {object} state
     */
    async create({ commit, state }) {
        const successMessage = i18n.t('users.create.successMessage');

        commit(LOADING);

        // Api request to create business
        const resp = await users.create(state.user);

        if (resp.ok) {
            // Reset store id successfully created User
            commit(RESET_USER);
        }

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.users');

        commit(LOADING);
    },
    /**
     * Get User by Id.
     * @param {function} commit
     * @param {string} id
     */
    async read({ commit }, id) {
        commit(LOADING);

        // Api request get business account by id
        const resp = await users.read(id);

        if (resp.ok) {
            // Set data to store
            commit(SET_USER, resp.data);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }

        commit(LOADING);

        return resp.ok;
    },

    /**
     * Update User account
     * @param {function} commit
     * @param {object} state
     * @param {string} id
     * @returns {Promise<void>}
     */
    async update({ commit, state }) {
        const successMessage = i18n.t('users.edit.successMessage');

        commit(LOADING);

        const { id } = state.user;

        // Update User information
        const resp = await users.update(id, state.user);

        // Handle response and show message
        Handler.handleResponse(resp, successMessage, '', 'dashboard.users');

        // Reset store User if account updated
        if (resp.ok) {
            commit(RESET_USER);
        }

        commit(LOADING);

        return resp.ok;
    },

    /**
     * Delete User account.
     * @param {function} commit
     * @param {function} dispatch
     * @param {string} id
     * @returns {Promise<void>}
     */
    async deleteAccount({ commit, dispatch, rootGetters }, id) {
        const successMessage = i18n.t(
            'users.edit.settings.delete.successMessage'
        );

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.delete'),
            { root: true }
        );

        // Check if not confirmed - stop deleting process
        if (!confirmed) return;

        commit(LOADING);

        const localUser = rootGetters['auth/user'];

        let params;
        let resp;
        if (localUser.role === 'admin') {
            params = { id };

            resp = await users.deleteAsAdmin(params);
        } else {
            params = {
                id,
                businessId: localUser.business.id
            };

            resp = await users.deleteAsChampion(params);
        }

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.users');

        // Reset store business if account deleted
        if (resp.ok) {
            commit(RESET_USER);
        }

        commit(LOADING);

        return resp.ok;
    },

    /**
     * Change Business for account.
     * @param {function} commit
     * @param {function} dispatch
     * @param {string} id
     * @returns {Promise<void>}
     */
    async changeBusiness({ commit, dispatch }, id) {
        const successMessage = i18n.t(
            'users.edit.settings.change.successMessage'
        );

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('users.edit.settings.change'),
            { root: true }
        );

        // Check if not confirmed - stop deleting process
        if (!confirmed) return;

        commit(LOADING);

        const resp = await users.changeToBtc(id);

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.users');

        commit(LOADING);

        return resp.ok;
    },

    /**
     * Activate/Deactivate account
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<void>}
     */
    updateStatus: async function({ commit, state }) {
        const { isActive, id } = state.user;

        // Set success message based on value isActive
        let successMessage = isActive
            ? i18n.t('users.edit.settings.deactivate.successMessageActivated')
            : i18n.t(
                  'users.edit.settings.deactivate.successMessageDeactivated'
              );

        commit(LOADING);

        // Api request on update status
        const resp = await users.updateStatus(id, { isActive });

        // Handle response
        Handler.handleResponse(resp, successMessage);

        commit(LOADING);
    },

    /**
     * Activate/Deactivate account
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<void>}
     */
    setRating: async function({ commit }, data) {
        const { id, rating, comment } = data;

        if (!rating) {
            // Handle response
            Handler.handleResponse(
                { ok: false, status: 422 },
                '',
                'Please submit your feedback on scale above.'
            );
            return;
        }

        // Set success message based on value isActive
        let successMessage = i18n.t('rateChallenge.successMessage');

        commit(LOADING);

        // Api request on update status
        const resp = await users.setRating(id, { rating, comment });

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.sessions');

        commit(LOADING);
    },

    /**
     * Reset account password
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     */
    async resetPassword({ commit, dispatch, state }) {
        const successMessage = i18n.t(
            'users.edit.settings.reset.successMessage'
        );

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.reset'),
            { root: true }
        );

        if (!confirmed) return;

        commit(LOADING);

        // Api request on password reset
        const resp = await users.resetPassword(state.user.id);

        // Handle response
        Handler.handleResponse(resp, successMessage);

        commit(LOADING);
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [SET_USER](state, data) {
        state.user = data;
    },
    [SET_USERS](state, data) {
        state.users = data;
    },
    [RESET_USER](state) {
        state.user = { ...new User() };
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isLoading: state => state.isLoading
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
};
